import type React from 'react';
import { calcValue, hexAlpha, isEmpty, type HexAlpha } from '@nfctron/nexus-core';

/**
 * Tailwind supported base colors
 */
export const TAILWIND_COLORS = [
	'white',
	'black',
	'slate',
	'gray',
	'zinc',
	'neutral',
	'stone',
	'red',
	'orange',
	'amber',
	'yellow',
	'lime',
	'green',
	'emerald',
	'teal',
	'cyan',
	'sky',
	'blue',
	'indigo',
	'violet',
	'purple',
	'fuchsia',
	'pink',
	'rose',
] as const;
export type TailwindColor = (typeof TAILWIND_COLORS)[number];

/**
 * NFCtron branded colors
 */
export const NFCTRON_COLORS = ['purple-heart', 'lucky-point'] as const;
export type NfctronColor = (typeof NFCTRON_COLORS)[number];

/**
 * All theme supported colors
 */
export const THEME_COLORS = [...TAILWIND_COLORS, ...NFCTRON_COLORS] as const;
export type ThemeColor = (typeof THEME_COLORS)[number];

/**
 * Returns true if the given color is a theme color
 * @param color A color name to check
 * @returns Whether the color is a valid theme color
 */
export const isThemeColor = (color: string | undefined | null): color is ThemeColor => THEME_COLORS.includes(color as ThemeColor);

/**
 * Returns true if the given color is a HEX color with an optional alpha channel
 * @param color A color string to check
 * @returns Whether the color is a valid HEX-A color
 */
export const isHexAlpha = (color: string | undefined | null): color is HexAlpha =>
	/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})([A-Fa-f0-9]{2})?$/.test(color ?? '');

/* -------------------------------------------------------------------------------------------------
 * Tailwind theme helpers
 * -----------------------------------------------------------------------------------------------*/
type TwColorName = ThemeColor;
type TwColorShade = 25 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;
type TwBoxShadow = 'splash';
type TwFontSize = 'xxs' | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
type TwBorderRadius = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
type TwFontWeight = 'normal' | 'medium' | 'semibold' | 'bold';

export type CssVariablesRecord = Record<string, string>;
export type TVariableKeys<TVariables extends CssVariablesRecord> = Exclude<keyof TVariables, number | symbol>;
/**
 * Extracts color keys from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all color names without their shade numbers
 */
export type TVariableColorKeys<TVariables extends CssVariablesRecord> = TVariables extends unknown
	? {
			[K in keyof TVariables as K extends `color-${infer ColorPart}-${number}`
				? ColorPart
				: K extends `color-${infer ColorPart1}-${infer ColorPart2}-${number}`
				? `${ColorPart1}-${ColorPart2}`
				: never]: true;
	  } extends infer R
		? keyof R
		: never
	: never;

/**
 * Extracts color shade numbers from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all available color shade numbers
 */
export type TVariableColorShadeKeys<TVariables extends CssVariablesRecord> = Extract<
	TVariableKeys<TVariables>,
	`color-${string}-${number}`
> extends `color-${string}-${infer T}`
	? T
	: never;

/**
 * Extracts font size keys from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all available font sizes excluding line height variables
 */
export type TVariableFontSizeKeys<TVariables extends CssVariablesRecord> = Extract<
	TVariableKeys<TVariables>,
	`text-${string}`
> extends `text-${infer T}`
	? T extends `${infer Base}--line-height` | `shadow-${string}`
		? never
		: T
	: never;

/**
 * Extracts font weight keys from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all available font weights
 */
export type TVariableFontWeightKeys<TVariables extends CssVariablesRecord> = Extract<
	TVariableKeys<TVariables>,
	`font-weight-${string}`
> extends `font-weight-${infer T}`
	? T
	: never;

/**
 * Extracts box shadow keys from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all available box shadow styles
 */
export type TVariableBoxShadowKeys<TVariables extends CssVariablesRecord> = Extract<
	TVariableKeys<TVariables>,
	`shadow-${string}`
> extends `shadow-${infer T}`
	? T
	: never;

/**
 * Extracts border radius keys from CSS variables
 * @template TVariables - CSS variables record type
 * @returns Union type of all available border radius sizes
 */
export type TVariableBorderRadiusKeys<TVariables extends CssVariablesRecord> = Extract<
	TVariableKeys<TVariables>,
	`radius-${string}`
> extends `radius-${infer T}`
	? T
	: never;

export class TailwindThemeHelper<TVariables extends Record<string, string>> {
	constructor(public variables: TVariables) {}

	/**
	 * Returns a CSS variable value for any CSS variable
	 * @param variableName - The CSS variable name with or without the `--` prefix
	 * @returns The CSS variable value or undefined if not found
	 */
	public getCssVariableValue = (variableName: TVariableKeys<TVariables> | string): string | undefined => {
		/** normalize the CSS variable name */
		const cssVarName = variableName.startsWith('--') ? variableName.substring(2) : variableName;

		/** handle SSR case or fallback to variables provided in constructor */
		if (typeof window === 'undefined' || typeof document === 'undefined') {
			return this.variables[cssVarName];
		}

		try {
			/** get the CSS variable value from document */
			const cssVarNameWithPrefix = cssVarName.startsWith('--') ? cssVarName : `--${cssVarName}`;
			const value = getComputedStyle(document.documentElement).getPropertyValue(cssVarNameWithPrefix).trim();

			/** if value is empty, fallback to variables */
			if (isEmpty(value)) return this.variables[cssVarName];

			return value;
		} catch (error) {
			/** fallback to variables if there's an error */
			return this.variables[cssVarName];
		}
	};

	/**
	 * Type-safe function to get a specific color with shade from CSS variables
	 * @param colorName - Base color name (e.g. 'lucky-point', 'purple-heart')
	 * @param shade - Color shade (25-950)
	 * @param asHex - Whether to return the value as a HEX color string
	 * @returns The CSS variable value, HEX color string, or fallback color if not found
	 */
	public getThemeColor = <TAsHex extends boolean = true>(
		colorName: TVariableColorKeys<TVariables> | TwColorName,
		shade: TwColorShade = 500,
		asHex: TAsHex = true as TAsHex,
	): TAsHex extends true ? HexAlpha : string => {
		const fallbackColor = hexAlpha('000000FF');
		const variableName = calcValue(() => {
			switch (colorName) {
				case 'black':
				case 'white':
					/** for black and white, no shade is needed */
					return `color-${colorName}`;
				default:
					return `color-${colorName}-${shade}`;
			}
		});

		const candidateValue = this.getCssVariableValue(variableName);

		/** return fallback if value not found */
		if (isEmpty(candidateValue)) return fallbackColor;

		/** convert to HEX if requested */
		if (asHex) return hexAlpha(candidateValue);

		/** return raw CSS variable value */
		return candidateValue as any;
	};

	/**
	 * Type-safe function to get a specific font size from CSS variables
	 * @param size - Font size name (e.g. 'xs', 'sm', 'base')
	 * @returns The CSS variable value or fallback if not found
	 */
	public getFontSize = (size: TVariableFontSizeKeys<TVariables> | TwFontSize) => {
		const fallbackFontSize = { fontSize: '14px' };
		const textVariableName = `text-${size}`;
		const lineHeightVariableName = `text-${size}--line-height`;
		const candidateValue = this.getCssVariableValue(textVariableName);
		if (isEmpty(candidateValue)) return fallbackFontSize;

		/** try to extract line height if defined in CSS variable format "fontSize/lineHeight" */
		if (candidateValue.includes('/')) {
			const parts = candidateValue.split('/');
			const fontSize = parts[0];
			const lineHeight = parts[1];
			if (!isEmpty(fontSize) && !isEmpty(lineHeight)) {
				return {
					fontSize: fontSize.trim(),
					lineHeight: lineHeight.trim(),
				};
			}
		}

		/** check for separate line height variable */
		const lineHeightValue = this.getCssVariableValue(lineHeightVariableName);
		if (!isEmpty(lineHeightValue)) {
			return {
				fontSize: candidateValue,
				lineHeight: lineHeightValue,
			};
		}

		return { fontSize: candidateValue };
	};

	/**
	 * Type-safe function to get a specific box shadow value from CSS variables
	 * @param shadow - Box shadow name (e.g. 'splash', 'sm', 'md')
	 * @returns The CSS variable value or fallback if not found
	 */
	public getBoxShadow = (shadow: TVariableBoxShadowKeys<TVariables> | TwBoxShadow): string => {
		const fallbackShadow = 'none';
		const variableName = `shadow-${shadow}`;

		const candidateValue = this.getCssVariableValue(variableName);
		if (isEmpty(candidateValue)) return fallbackShadow;

		return candidateValue;
	};

	/**
	 * Type-safe function to get a specific border radius value from CSS variables
	 * @param radius - Border radius name (e.g. 'xs', 'sm', 'md')
	 * @returns The CSS variable value or fallback if not found
	 */
	public getBorderRadius = (radius: TVariableBorderRadiusKeys<TVariables> | TwBorderRadius): string => {
		const fallbackRadius = '0';
		const variableName = `radius-${radius}`;

		const candidateValue = this.getCssVariableValue(variableName);
		if (isEmpty(candidateValue)) return fallbackRadius;

		return candidateValue;
	};

	/**
	 * Type-safe function to get a specific font weight value from CSS variables
	 * @param weight - Font weight name (e.g. 'normal', 'medium', 'bold')
	 * @returns The CSS variable value or fallback if not found
	 */
	public getFontWeight = (weight: TVariableFontWeightKeys<TVariables> | TwFontWeight): string => {
		const fallbackWeight = '400';
		const variableName = `font-weight-${weight}`;

		const candidateValue = this.getCssVariableValue(variableName);
		if (isEmpty(candidateValue)) return fallbackWeight;

		return candidateValue;
	};
}

/* -------------------------------------------------------------------------------------------------
 * Dynamic color classes / style
 * TODO: migrate to tailwindcss@4.x
 * -----------------------------------------------------------------------------------------------*/
type TwColorClass = 'bg' | 'text' | 'border' | 'ring';
/**
 * Returns dynamic color TailwindCSS classes based on the given theme, shade and classes (bg, text, border, ring)
 * ⚠️ NOTE: Relies on having `safeList` enabled in the TailwindCSS configuration.
 * @param color A valid theme color name
 * @param shade Shade of the color (25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900)
 * @param classes TailwindCSS classes to apply the color to (bg, text, border, ring)
 * @returns TailwindCSS classes string
 */
export const getThemeColorClasses = (color: ThemeColor, shade: TwColorShade, ...classes: TwColorClass[]) =>
	classes.map((c) => `${c}-${color}-${shade}`).join(' ');

/**
 * Returns color TailwindCSS className and style based on the given input color.
 * The input color may be a theme color or a HEX color with an optional alpha channel.
 * ⚠️ NOTE: Relies on having `safeList` enabled in the TailwindCSS configuration.
 * @param color Input color (theme color or HEX color)
 * @param twShade Optional color shade when applying theme color, defaults to 500
 * @param apply TailwindCSS classes to apply the color to (bg, text, border, ring)
 * @return {Partial<{className: string, style: React.CSSProperties}>}
 */
export const getPropsFromColor = (
	color: ThemeColor | HexAlpha | undefined | null,
	twShade: TwColorShade = 500,
	...apply: TwColorClass[]
): Partial<{
	className: string;
	style: React.CSSProperties;
}> => {
	if (isThemeColor(color)) {
		return { className: getThemeColorClasses(color, twShade, ...apply) };
	}

	if (isHexAlpha(color)) {
		const style = apply.reduce<React.CSSProperties>((acc, twClass) => {
			switch (twClass) {
				case 'bg':
					acc.backgroundColor = color;
					break;
				case 'text':
					acc.color = color;
					break;
				case 'border':
					acc.borderColor = color;
					break;
				case 'ring':
					acc['--ring-color' as any] = color;
					break;
			}
			return acc;
		}, {});

		return { style };
	}

	return {};
};
